import { saveAs } from "file-saver";
import React from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import Layout from "../../components/Layout/Layout";
import Loading from "../../components/Loading";
import Table from "../../components/Table";
import { deleteInvoice, getAllInvoices, getJson, sendInvoiceWhatsappApi } from "../../utils/api";

import { Button } from "@mui/material";
import { toast } from "react-toastify";
import DeleteModal from "../../components/modals/DeleteModal";
import useStore from "../../utils/store";

const Invoices = () => {
  const [invoices, setInvoices] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [loading2, setLoading2] = React.useState(false);
  const startDate = useStore((state) => state.startDate);
  const endDate = useStore((state) => state.endDate);
  const getData = async () => {
    setLoading(true);
    const [success, data] = await getAllInvoices(startDate, endDate);
    if (success) {
      setInvoices(data);
    }
    setLoading(false);
  };

  const jsonFileDownload = async () => {
    // const json_data = {
    //   name: "Dedar",
    //   age: "14",
    //   address: "House #28",
    // };
    setLoading(true);
    const [success, json_data] = await getJson(startDate, endDate);
    console.log(json_data);
    if (success) {
      const fileName = `invoices${new Date().getTime()}.json`;
      const data = new Blob([JSON.stringify(json_data)], { type: "text/json" });
      const jsonURL = window.URL.createObjectURL(data);
      const link = document.createElement("a");
      document.body.appendChild(link);
      link.href = jsonURL;
      link.setAttribute("download", fileName);
      link.click();
      document.body.removeChild(link);
    }
    setLoading(false);
  };

  const excelFileDownload = async () => {
    if (!invoices || invoices.length === 0) {
      toast.error("No invoice data available to export");
      return;
    }
    setLoading(true);
    try {
      // Fetch structured GST data (hsn + doc_issue) from backend
      const [gstOk, gstData] = await getJson(startDate, endDate);

      const wb = XLSX.utils.book_new();

      // ── SHEET 1: b2b,sez,de ────────────────────────────────────────
      const fmtDate = (d) => {
        if (!d) return "";
        const dt = new Date(d);
        if (isNaN(dt)) return "";
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        return `${String(dt.getDate()).padStart(2, "0")}-${months[dt.getMonth()]}-${dt.getFullYear()}`;
      };

      const b2bRows = invoices.map((inv) => {
        const gstin = inv.customer?.customer?.gstNumber || inv.customer?.gstNumber || "";
        const name = inv.customer?.customer?.userName || inv.customer?.userName || "";
        const preGst = inv.preGstTotal || 0;
        const cgst = inv.cgst || 0;
        const sgst = inv.sgst || 0;
        const rate = preGst > 0 ? ((cgst + sgst) / preGst) * 100 : 0;
        let invoiceType = inv.invoiceType || "";
        if (!invoiceType) {
          invoiceType = inv.isSEZ ? "SEZ supplies without payment" : inv.isDeemedExport ? "Deemed Exp" : "Regular B2B";
        }
        return [
          gstin,
          name,
          inv.invoiceNumberString || "",
          fmtDate(inv.invoiceDate),
          parseFloat((inv.grandTotal || 0).toFixed(2)),
          inv.customer?.customer?.placeOfSupply || inv.customer?.placeOfSupply || "03-Punjab",
          inv.reverseCharge === "Y" || inv.reverseCharge === true ? "Y" : "N",
          "", // Applicable % of Tax Rate - keep blank as requested
          invoiceType,
          inv.eCommerceGstin || inv.ecommerceGstin || "",
          rate > 0 ? parseFloat(rate.toFixed(2)) : "",
          parseFloat(preGst.toFixed(2)),
          parseFloat(((inv.cess || inv.cessAmount || 0)).toFixed(2)) || "",
        ];
      });

      const uniqueRecipients = new Set(
        invoices.map((inv) => inv.customer?.customer?.gstNumber || inv.customer?.gstNumber || inv.customer?.customer?._id || "")
      ).size;
      const totalInvoiceValue = parseFloat(invoices.reduce((s, inv) => s + (inv.grandTotal || 0), 0).toFixed(2));
      const totalTaxableValue = parseFloat(invoices.reduce((s, inv) => s + (inv.preGstTotal || 0), 0).toFixed(2));
      const totalCess = parseFloat(invoices.reduce((s, inv) => s + (inv.cess || inv.cessAmount || 0), 0).toFixed(2));

      // Columns: A=GSTIN B=Name C=InvNo D=Date E=InvValue F=PoS G=RC H=Applic% I=Type J=Ecomm K=Rate L=Taxable M=Cess
      const b2bAoa = [
        // Row 1 – title
        ["Summary For B2B, SEZ, DE [4A, 4B, 6B, 6C]", "", "", "", "", "", "", "", "", "", "", "", ""],
        // Row 2 – summary LABELS exactly above their columns (Col A, C, E, L, M)
        ["No. of Recipients", "", "No. of Invoices", "", "Total Invoice Value", "", "", "", "", "", "", "Total Taxable Value", "Total Cess"],
        // Row 3 – summary VALUES exactly under their labels
        [uniqueRecipients, "", invoices.length, "", totalInvoiceValue, "", "", "", "", "", "", totalTaxableValue, totalCess],
        // Row 4 – column headers
        ["GSTIN/UIN of Recipient", "Receiver Name", "Invoice Number", "Invoice date", "Invoice Value", "Place Of Supply", "Reverse Charge", "Applicable % of Tax Rate", "Invoice Type", "E-Commerce GSTIN", "Rate", "Taxable Value", "Cess Amount"],
        // Rows 5+ – data
        ...b2bRows,
      ];

      const ws1 = XLSX.utils.aoa_to_sheet(b2bAoa);
      XLSX.utils.book_append_sheet(wb, ws1, "b2b,sez,de");

      // ── SHEET 2: hsn(b2b) ──────────────────────────────────────────
      const hsnList = gstOk ? (gstData?.hsn?.hsn_b2b || []) : [];
      const hsnTotalValue = parseFloat(hsnList.reduce((s, h) => s + (h.txval || 0) + (h.camt || 0) + (h.samt || 0) + (h.iamt || 0), 0).toFixed(2));
      const hsnTaxable = parseFloat(hsnList.reduce((s, h) => s + (h.txval || 0), 0).toFixed(2));
      const hsnIgst = parseFloat(hsnList.reduce((s, h) => s + (h.iamt || 0), 0).toFixed(2));
      const hsnCgst = parseFloat(hsnList.reduce((s, h) => s + (h.camt || 0), 0).toFixed(2));
      const hsnSgst = parseFloat(hsnList.reduce((s, h) => s + (h.samt || 0), 0).toFixed(2));
      const hsnCess = parseFloat(hsnList.reduce((s, h) => s + (h.csamt || 0), 0).toFixed(2));

      const hsnAoa = [
        // Row 1 – title + HELP
        ["Summary For HSN[12]", "", "", "", "", "", "", "", "", "", "HELP"],
        // Row 2 – summary labels directly above corresponding columns
        ["No. of HSN", "", "", "", "Total Value", "", "Total Taxable Value", "Total Integrated Tax", "Total Central Tax", "Total State/UT Tax", "Total Cess"],
        // Row 3 – summary values directly under their labels
        [hsnList.length, "", "", "", hsnTotalValue, "", hsnTaxable, hsnIgst, hsnCgst, hsnSgst, hsnCess],
        // Row 4 – column headers
        ["HSN", "Description", "UQC", "Total Quantity", "Total Value", "Rate", "Taxable Value", "Integrated Tax Amount", "Central Tax Amount", "State/UT Tax Amount", "Cess Amount"],
        // Rows 5+ – data
        ...hsnList.map((h) => {
          const totalVal = parseFloat(((h.txval || 0) + (h.camt || 0) + (h.samt || 0) + (h.iamt || 0)).toFixed(2));
          let uqc = h.uqc || "PCS";
          uqc = uqc.toUpperCase();
          if (uqc === "PCS") uqc = "PCS-PIECES";
          else if (uqc === "KGS") uqc = "KGS-KILOGRAMS";
          else if (uqc === "NOS") uqc = "NOS-NUMBERS";
          else if (uqc === "MTR" || uqc === "MTRS") uqc = "MTR-METERS";
          else if (uqc === "LTR" || uqc === "LTRS") uqc = "LTR-LITRES";
          
          return [
            h.hsn_sc || "",
            h.desc || "",
            uqc,
            h.qty || 0,
            totalVal,
            h.rt || 0,
            parseFloat((h.txval || 0).toFixed(2)),
            parseFloat((h.iamt || 0).toFixed(2)),
            parseFloat((h.camt || 0).toFixed(2)),
            parseFloat((h.samt || 0).toFixed(2)),
            parseFloat((h.csamt || 0).toFixed(2)),
          ];
        }),
      ];

      const ws2 = XLSX.utils.aoa_to_sheet(hsnAoa);
      XLSX.utils.book_append_sheet(wb, ws2, "hsn(b2b)");

      // ── SHEET 3: docs ──────────────────────────────────────────────
      const docDet = gstOk ? (gstData?.doc_issue?.doc_det || []) : [];
      const docRows = [];
      docDet.forEach((det) => {
        (det.docs || []).forEach((d) => {
          docRows.push([
            det.doc_typ || "Invoices for outward supply",
            d.from || "",
            d.to || "",
            d.totnum || 0,
            d.cancel || 0,
          ]);
        });
      });
      const totalDocs = docRows.reduce((s, r) => s + (r[3] || 0), 0);
      const totalCancelled = docRows.reduce((s, r) => s + (r[4] || 0), 0);

      const docsAoa = [
        // Row 1 – title and HELP text
        ["Summary of documents issued during the tax period [13]", "", "", "", "HELP"],
        // Row 2 – summary labels aligned with Col D and E
        ["", "", "", "Total Number", "Total Cancelled"],
        // Row 3 – values exactly below the labels
        ["", "", "", totalDocs, totalCancelled],
        // Row 4 – column headers (aligns with summary above)
        ["Nature of Document", "Sr. No. From", "Sr. No. To", "Total Number", "Cancelled"],
        // Rows 5+ – data
        ...docRows,
      ];

      const ws3 = XLSX.utils.aoa_to_sheet(docsAoa);
      XLSX.utils.book_append_sheet(wb, ws3, "docs");

      // ── Download ───────────────────────────────────────────────────
      const fileName = `GSTR1_${new Date().getTime()}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success("Excel file downloaded successfully");
    } catch (error) {
      console.error("Error exporting Excel:", error);
      toast.error("Failed to export Excel file");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    getData();
  }, [startDate, endDate]);

  // Calculate total invoice amount
  const totalInvoiceAmount = invoices.reduce((total, invoice) => {
    return total + (invoice.grandTotal || 0);
  }, 0);

  const totalPreGSTInvoiceAmount = invoices.reduce(
    (preGSTInvoiceTotal, invoice) => {
      return preGSTInvoiceTotal + (invoice.preGstTotal || 0);
    },
    0
  );

  return (
    <Layout>
      {loading && <Loading />}
      <div className="flex gap-3 mb-4">
        <Button onClick={jsonFileDownload} variant="contained">
          Download Json
        </Button>
        <Button onClick={excelFileDownload} variant="contained" color="success">
          Download Excel (GSTR-1)
        </Button>
      </div>
      <div className="mt-5 mb-3 font-bold">
        Total Invoice Amount: ₹ {totalInvoiceAmount.toFixed(2)}
      </div>
      <div className="mt-5 mb-3 font-bold">
        Total Invoice Amount (Without Gst): ₹{" "}
        {totalPreGSTInvoiceAmount.toFixed(2)}
      </div>
      <Table
        columns={[
          {
            title: "INVOICE No.",
            dataIndex: "invoiceNumberString",
          },
          {
            title: "DATE",
            dataIndex: "invoiceDate",
            render: (date) =>
              new Date(date?.invoiceDate).toLocaleDateString("en-GB"),
          },
          {
            title: "CUSTOMER",
            dataIndex: "customer",
            render: (customer) => <>{customer?.customer?.userName}</>,
          },
          {
            title: "TOTAL",
            dataIndex: "grandTotal",
            render: (total) => <>{total?.grandTotal?.toFixed(2)}</>,
          },
          {
            title: "WhatsApp",
            render: (row) => (
              <div className="flex gap-2 items-center">
                <Button
                  variant="outlined"
                  size="small"
                  color="success"
                  style={{ textTransform: "none", fontSize: "11px", padding: "2px 8px" }}
                  onClick={async () => {
                    toast.info("Sending Invoice PDF to WhatsApp...");
                    const [success, res] = await sendInvoiceWhatsappApi(row._id, row?.customer?._id);
                    if (success) {
                      toast.success("✅ Invoice PDF Sent to WhatsApp Successfully!");
                    } else {
                      toast.error(res || "Failed to send WhatsApp");
                    }
                  }}
                >
                  Send PDF
                </Button>
                <Link
                  to={`/admin/invoice/${row._id}`}
                  target="_blank"
                  className="text-primary underline text-xs ml-1"
                >
                  View
                </Link>
              </div>
            ),
          },
          {
            title: "Print",
            render: (row) => (
              <div className="flex gap-2">
                <Link
                  to={`/admin/invoice/${row._id}?type=white`}
                  target="_blank"
                  className="text-black"
                >
                  View Invoice
                </Link>
              </div>
            ),
          },
          {
            title: "Action",
            render: (row) => (
              <DeleteModal
                loading={loading2}
                onClick={async () => {
                  setLoading2(true);
                  const [success, data] = await deleteInvoice(row._id);
                  if (success) {
                    toast.success("Invoice Deleted Successfully");
                    getData();
                  }
                  setLoading2(false);
                }}
              />
            ),
          },
        ]}
        data={invoices}
      />
    </Layout>
  );
};

export default Invoices;
